/**
 * Redrawing the figures when the view changes.
 *
 * A styled element restyles itself; a canvas does not. Switching to the
 * daylight view repaints every panel on the page and leaves every instrument
 * showing the pixels it drew for a dark room — captions at near-white on white,
 * tracks in near-black. The figure looks broken, and the cause is invisible
 * because the code that draws it is perfectly correct.
 *
 * So the switch announces itself, and each screen redraws whatever it owns.
 * An event rather than a shared callback because the screens do not know about
 * each other, and only one of them is mounted at a time.
 */
import { useEffect } from "react";

const EVENT = "skylogics:repaint";

/** Tell every mounted screen to redraw its figures. */
export function requestRepaint(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(EVENT));
}

/**
 * Redraw when the view changes.
 *
 * `draw` is held in a ref by the caller's own closure, so pass a stable
 * function or accept that the listener is rebound on every render — rebinding
 * is cheap, and a stale closure drawing the previous screen's data is not.
 */
export function useRepaint(draw: () => void): void {
  useEffect(() => {
    const run = () => draw();
    window.addEventListener(EVENT, run);
    return () => window.removeEventListener(EVENT, run);
  }, [draw]);
}
